import type {
  AdminStatus, ApiToken, Attachment, Board, Comment, DeliverySettings,
  EmailSettingsInput, ExportResult, Invitation, Member, NotificationPref,
  Priority, Project, ProjectRole, Role, SlackIdentity, SlackSettingsInput,
  Scope, Status, Subtask, Tag, Task, TaskDetail, User,
} from './types'

/**
 * ApiError carries the server's field-level messages so a form can mark the
 * offending input instead of showing a banner over everything.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly fields: Record<string, string> = {},
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** True when the session expired and the app should return to sign-in. */
  get isUnauthorized() {
    return this.status === 401
  }

  /**
   * The sentence worth showing somebody.
   *
   * A validation failure's `message` is the generic "check the highlighted
   * fields", which is right next to a form that can highlight them and useless
   * in a toast. The field messages are the ones that say what actually went
   * wrong, so anything without a form to mark up should show those.
   */
  get detail(): string {
    const fieldMessages = Object.values(this.fields ?? {}).filter(Boolean)
    return fieldMessages.length > 0 ? fieldMessages.join(' · ') : this.message
  }
}

/** The message to put in a toast for any thrown value. */
export function errorText(err: unknown): string | undefined {
  if (err instanceof ApiError) return err.detail
  if (err instanceof Error) return err.message
  return undefined
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const isForm = init.body instanceof FormData
  const res = await fetch(`/api${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: {
      ...(isForm ? {} : { 'Content-Type': 'application/json' }),
      ...init.headers,
    },
  })

  if (res.status === 204) return undefined as T

  const text = await res.text()
  const body = text ? JSON.parse(text) : null

  if (!res.ok) {
    const err = body?.error
    throw new ApiError(res.status, err?.message ?? `Request failed (${res.status})`, err?.fields ?? {})
  }
  return body as T
}

const get = <T,>(path: string) => request<T>(path)
const post = <T,>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body === undefined ? '{}' : JSON.stringify(body) })
const patch = <T,>(path: string, body: unknown) =>
  request<T>(path, { method: 'PATCH', body: JSON.stringify(body) })
const put = <T,>(path: string, body: unknown) =>
  request<T>(path, { method: 'PUT', body: JSON.stringify(body) })
const del = (path: string) => request<void>(path, { method: 'DELETE' })

/**
 * A partial task update. Every field is optional; sending `null` for assignee
 * or due date clears it, while omitting the field leaves it untouched. That
 * distinction is what lets the card menu change one attribute at a time.
 */
export interface TaskPatch {
  title?: string
  description?: string
  priority?: Priority
  assigneeId?: string | null
  dueDate?: string | null
  statusId?: string
  tagIds?: string[]
}

export interface TaskCreate extends TaskPatch {
  projectId: string
  /**
   * Names the column instead of identifying it, for a merged board whose
   * columns belong to several projects. Ignored when statusId is given.
   */
  statusName?: string
}

export const api = {
  // ------------------------------------------------------------------ auth
  login: (email: string, password: string) =>
    post<{ user: User }>('/auth/login', { email, password }),
  logout: () => post<{ status: string }>('/auth/logout'),
  logoutEverywhere: () => post<{ status: string }>('/me/logout-all'),
  me: () => get<{ user: User; slackConfigured: boolean; emailConfigured: boolean }>('/me'),

  requestReset: (email: string) => post<{ status: string }>('/auth/password-reset', { email }),
  confirmReset: (token: string, password: string) =>
    post<{ status: string }>('/auth/password-reset/confirm', { token, password }),

  invitePreview: (token: string) => get<{ email: string; name: string }>(`/auth/invite/${token}`),
  acceptInvite: (token: string, name: string, password: string) =>
    post<{ user: User }>(`/auth/invite/${token}/accept`, { name, password }),

  updateProfile: (body: {
    name: string; timezone: string; avatarUrl: string; digestEmail: boolean
  }) => patch<User>('/me', body),
  changePassword: (currentPassword: string, newPassword: string) =>
    post<{ status: string }>('/me/password', { currentPassword, newPassword }),

  prefs: () => get<{ prefs: NotificationPref[]; labels: Record<string, string> }>('/me/prefs'),
  setPrefs: (prefs: NotificationPref[]) =>
    put<{ prefs: NotificationPref[]; labels: Record<string, string> }>('/me/prefs', { prefs }),

  tokens: () => get<ApiToken[]>('/me/tokens'),
  createToken: (name: string, scopes: Scope[], projectIds: string[]) =>
    post<{ token: ApiToken; secret: string; mcpUrl: string }>('/me/tokens', {
      name, scopes, projectIds,
    }),
  deleteToken: (id: string) => del(`/me/tokens/${id}`),

  slackIdentity: () =>
    get<{ linked: boolean; identity: SlackIdentity; canConnect: boolean }>('/me/slack'),
  unlinkSlack: () => del('/me/slack'),

  // ----------------------------------------------------------------- users
  users: () => get<User[]>('/users'),

  // ------------------------------------------------------------------ tags
  tags: () => get<Tag[]>('/tags'),
  createTag: (name: string, color: string) => post<Tag>('/tags', { name, color }),
  updateTag: (id: string, name: string, color: string) => patch<Tag>(`/tags/${id}`, { name, color }),
  deleteTag: (id: string) => del(`/tags/${id}`),

  // -------------------------------------------------------------- projects
  projects: (archived = false) => get<Project[]>(`/projects?archived=${archived}`),
  project: (id: string) => get<Project>(`/projects/${id}`),
  projectByKey: (key: string) => get<Project>(`/projects/by-key/${key}`),
  createProject: (body: {
    key: string; name: string; description: string; color: string
    parentId?: string | null
  }) => post<Project>('/projects', body),
  updateProject: (id: string, body: {
    key?: string; name: string; description: string; color: string
    slackChannelId?: string; slackEvents?: string[]
    /** Omit to leave the parent alone; send null to move it to the top level. */
    parentId?: string | null
    /** Same rule: omit to leave it, null to clear it. */
    defaultAssigneeId?: string | null
  }) => patch<Project>(`/projects/${id}`, body),
  archiveProject: (id: string, archived: boolean) =>
    post<Project>(`/projects/${id}/archive`, { archived }),
  deleteProject: (id: string) => del(`/projects/${id}`),
  archiveDone: (id: string, olderThanDays: number) =>
    post<{ archived: number }>(`/projects/${id}/archive-done`, { olderThanDays }),

  board: (id: string, query: string) => get<Board>(`/projects/${id}/board${query}`),

  members: (id: string) => get<Member[]>(`/projects/${id}/members`),
  addMember: (id: string, userId: string, role: ProjectRole) =>
    post<Member[]>(`/projects/${id}/members`, { userId, role }),
  removeMember: (id: string, userId: string) => del(`/projects/${id}/members/${userId}`),

  statuses: (id: string) => get<Status[]>(`/projects/${id}/statuses`),
  createStatus: (id: string, body: { name: string; color: string; isDone: boolean }) =>
    post<Status>(`/projects/${id}/statuses`, body),
  updateStatus: (id: string, statusId: string, body: { name: string; color: string; isDone: boolean }) =>
    patch<Status>(`/projects/${id}/statuses/${statusId}`, body),
  reorderStatuses: (id: string, order: string[]) =>
    put<Status[]>(`/projects/${id}/statuses/order`, { order }),
  deleteStatus: (id: string, statusId: string, moveTo?: string) =>
    del(`/projects/${id}/statuses/${statusId}${moveTo ? `?moveTo=${moveTo}` : ''}`),

  // ----------------------------------------------------------------- tasks
  tasks: (query: string) => get<Task[]>(`/tasks${query}`),
  task: (id: string) => get<TaskDetail>(`/tasks/${id}`),
  taskByKey: (key: string) => get<TaskDetail>(`/tasks/by-key/${key}`),
  createTask: (body: TaskCreate) => post<Task>('/tasks', body),
  updateTask: (id: string, body: TaskPatch) => patch<Task>(`/tasks/${id}`, body),
  deleteTask: (id: string) => del(`/tasks/${id}`),
  duplicateTask: (id: string) => post<Task>(`/tasks/${id}/duplicate`),
  archiveTask: (id: string, archived: boolean) =>
    post<Task>(`/tasks/${id}/archive`, { archived }),
  watchTask: (id: string, watching: boolean) =>
    post<TaskDetail>(`/tasks/${id}/watch`, { watching }),

  /**
   * Moves a card. Neighbours rather than an index, so a slightly stale column
   * on screen still lands the card exactly where it was dropped.
   *
   * On an aggregated board the destination is named rather than identified:
   * columns are merged across sub-projects, and the status row the card must
   * end up in belongs to the card's own project.
   */
  moveTask: (
    id: string,
    target: { statusId?: string; statusName?: string },
    afterId: string | null,
    beforeId: string | null,
  ) => post<Task>(`/tasks/${id}/move`, { ...target, afterId, beforeId }),

  /**
   * Moves a whole selection into one column, as a single transaction — a bulk
   * status change that half-applied would leave the board somewhere nobody
   * asked for.
   */
  bulkMove: (taskIds: string[], target: { statusId?: string; statusName?: string }) =>
    post<Task[]>('/tasks/bulk-move', { taskIds, ...target }),

  // --------------------------------------------------------------- subtasks
  //
  // Each item is addressed by its own id, so marking one done never touches
  // the others — the shape a script or an agent needs to report progress.
  subtasks: (taskId: string) => get<Subtask[]>(`/tasks/${taskId}/subtasks`),
  addSubtask: (taskId: string, title: string) =>
    post<Subtask>(`/tasks/${taskId}/subtasks`, { title }),
  updateSubtask: (id: string, body: { title?: string; done?: boolean }) =>
    patch<Subtask>(`/subtasks/${id}`, body),
  deleteSubtask: (id: string) => del(`/subtasks/${id}`),
  reorderSubtasks: (taskId: string, order: string[]) =>
    put<Subtask[]>(`/tasks/${taskId}/subtasks/order`, { order }),

  addComment: (taskId: string, body: string) =>
    post<Comment>(`/tasks/${taskId}/comments`, { body }),
  updateComment: (id: string, body: string) => patch<Comment>(`/comments/${id}`, { body }),
  deleteComment: (id: string) => del(`/comments/${id}`),

  uploadAttachment: (taskId: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return request<Attachment>(`/tasks/${taskId}/attachments`, { method: 'POST', body: form })
  },
  deleteAttachment: (id: string) => del(`/attachments/${id}`),
  attachmentUrl: (id: string) => `/api/attachments/${id}`,

  search: (q: string) => get<Task[]>(`/search?q=${encodeURIComponent(q)}`),

  // ---------------------------------------------------------- ai exports
  //
  // Exports carry development context only: link, description, attachment
  // links and discussion. Assignee, status, priority and the activity trail
  // are left out — they are how the board is run, not what somebody needs to
  // write the code.
  exportTask: (id: string) => get<ExportResult>(`/tasks/${id}/export`),
  exportTasks: (taskIds: string[]) => post<ExportResult>('/tasks/export', { taskIds }),

  // ----------------------------------------------------------------- admin
  adminUsers: () => get<User[]>('/admin/users'),
  setUserRole: (id: string, role: Role) => patch<User>(`/admin/users/${id}/role`, { role }),
  setUserActive: (id: string, active: boolean) =>
    patch<User>(`/admin/users/${id}/active`, { active }),
  invitations: () => get<Invitation[]>('/admin/invitations'),
  invite: (email: string, name: string, role: Role) =>
    post<Invitation>('/admin/invitations', { email, name, role }),
  deleteInvitation: (id: string) => del(`/admin/invitations/${id}`),
  adminStatus: () => get<AdminStatus>('/admin/status'),
  testEmail: () => post<{ status: string }>('/admin/test-email'),
  testSlack: () => post<{ status: string }>('/admin/test-slack'),

  settings: () => get<DeliverySettings>('/admin/settings'),
  saveEmailSettings: (body: EmailSettingsInput) =>
    put<DeliverySettings>('/admin/settings/email', body),
  saveSlackSettings: (body: SlackSettingsInput) =>
    put<DeliverySettings>('/admin/settings/slack', body),
}
